import { Button, Form } from 'antd';
import { Plus } from 'lucide-react';
import type { OrgUnitSummaryDto, PolicyListItemDto, RequirementLineInputDto } from '@pams/shared';
import { ForceGrid } from '@/components/intake/ForceGrid';
import { ExtrasSection, PlacesSection } from '@/components/intake/Sections';
import { mintShiftId, type ArrangementDraft } from '@/components/intake/draft';
import { fromGrid, remainingByColumn, type ForceGrid as ForceGridModel } from '@/lib/forceGrid';
import { coverageOf, coverageState, formatDuration } from '@/lib/shiftCoverage';
import type { SectionDescriptor, StepDescriptor } from '@/lib/sectionRegistry';
import { COVERAGE_LABEL, RTC_COPY } from './copy';
import { NoCommandNote, RunsForSection, ShiftPatternSection, StandingOrderBasics } from './components/Sections';
import type { StandingOrderEndsKind, StandingOrderFormValues } from './formValues';

/**
 * ─── THE ROUND-THE-CLOCK SECTION REGISTRY ────────────────────────────────────────
 *
 * ⚠️ THIS FILE IS THE PROOF THAT THE REGISTRY EARNS ITS KEEP.
 *
 * A second form, over the SAME shell (`components/intake/IntakeForm`) and mostly the SAME
 * section components, differing only in this array. Three sections are the PA intake's own
 * components rendered unchanged — `places`, `force` and `extras` — and the two that differ
 * differ because a standing order genuinely is not an arrangement, not because anything was
 * copied to make it work.
 *
 * ⚠️ AND `command` IS SIMPLY NOT IN THE ARRAY. Not hidden, not disabled, not an empty
 * roster: absent, so its fields are never registered and nothing can read a value from it.
 * That is the difference `sectionRegistry.ts`'s header draws between "absent from the
 * registry" and "`visibleWhen` false", and this is what it is for.
 */

export const RTC_STEPS = [
  { key: 'what', title: 'What and when' },
  { key: 'force', title: 'Force' },
  { key: 'orders', title: 'Instructions' },
] as const satisfies readonly StepDescriptor<string>[];

type StepKey = (typeof RTC_STEPS)[number]['key'];

export type StandingOrderSectionActions = {
  appendRow: <K extends 'locations' | 'shifts'>(field: K, row: StandingOrderFormValues[K][number]) => void;
  focusLastIn: (sectionId: string, selector: string) => void;
  setRequirementLines: (lines: RequirementLineInputDto[]) => void;
};

export type StandingOrderSectionState = {
  draft: ArrangementDraft;
  endsKind: StandingOrderEndsKind;
  grid: ForceGridModel;
  policies: readonly Pick<PolicyListItemDto, 'id' | 'name' | 'isDefault'>[];
  /** ⚠️ [D4.5 §1b] What an already-stored `policyId` renders as — archived ones included. */
  resolvePolicy: (id: string) => { name: string } | undefined;
  orgUnits: ReadonlyMap<string, OrgUnitSummaryDto>;
  /** ⚠️ FROM THE SERVER's preview, never a client constant. `null` until it has answered. */
  horizonDays: number | null;
  fillOpenLeadHours: number | null;
  readOnly: boolean;
};

export const STANDING_ORDER_SECTIONS: readonly SectionDescriptor<
  StandingOrderSectionState,
  StepKey,
  StandingOrderSectionActions
>[] = [
  {
    id: 'basics',
    step: 'what',
    title: 'Basics',
    validate: ({ draft }) => ({
      blockers: [
        ...(draft.policyId === '' ? ['Choose a policy.'] : []),
        ...(draft.title.trim() === '' ? ['Give this order a name.'] : []),
      ],
      warnings: [],
    }),
    render: (state) => (
      <StandingOrderBasics
        draft={state.draft}
        readOnly={state.readOnly}
        policies={state.policies}
        resolvePolicy={state.resolvePolicy}
      />
    ),
  },
  {
    /* ⚠️ THE PA INTAKE'S OWN COMPONENT, RENDERED UNCHANGED. */
    id: 'places',
    step: 'what',
    title: 'Places',
    validate: ({ draft }) => ({
      blockers: draft.placeCount > 0 ? [] : ['Add at least one place.'],
      warnings: [],
      ...(draft.placeCount > 0 ? { detail: `${String(draft.placeCount)} chosen` } : {}),
    }),
    render: (state) => (
      /* ⚠️ [amended D4] NO `showsLocationMode` — the prop is gone with the control. It was
         always FALSE here anyway: a standing order's places are a route, because a
         generated arrangement carries exactly one duty. */
      <PlacesSection draft={state.draft} readOnly={state.readOnly} />
    ),
    action: (state, actions) => (
      <Button
        size="small"
        htmlType="button"
        icon={<Plus className="size-3.5" />}
        disabled={state.readOnly}
        data-testid="add-place"
        onClick={() => {
          actions.appendRow('locations', { placeId: null, name: '' });
          actions.focusLastIn('places', '[data-testid^="location-"] input');
        }}
      >
        + Add another place
      </Button>
    ),
  },
  {
    id: 'shifts',
    step: 'what',
    title: 'Shift pattern',
    tooltip: RTC_COPY.coverageBar,
    /**
     * ⚠️ GAPS AND OVERLAPS ARE WARNINGS, NEVER BLOCKERS. An operator may have a reason, and
     * a form that blocks with no override is how people go back to keeping the real roster
     * in a spreadsheet. The only BLOCKER here is having no usable shift at all.
     */
    validate: ({ draft }) => {
      const coverage = coverageOf(draft.shifts);
      const state = coverageState(coverage);
      const warnings: string[] = [];
      if (state === 'GAPS') {
        warnings.push(`${formatDuration(coverage.gapMinutes)} of the day has nobody on duty.`);
      }
      if (coverage.overlapMinutes > 0) {
        warnings.push(`${formatDuration(coverage.overlapMinutes)} of the day is covered twice over.`);
      }
      return {
        blockers: state === 'NONE' ? ['Add at least one shift.'] : [],
        warnings,
        /* The rail shows the coverage from any step, so a hole is legible without opening this one. */
        detail: COVERAGE_LABEL[state],
      };
    },
    render: (state) => <ShiftPatternSection draft={state.draft} readOnly={state.readOnly} />,
    action: (state, actions) => (
      <Button
        size="small"
        htmlType="button"
        icon={<Plus className="size-3.5" />}
        disabled={state.readOnly}
        data-testid="add-shift"
        onClick={() => {
          actions.appendRow('shifts', { id: mintShiftId(), startTime: '', endTime: '' });
          /* ⚠️ The same focus-follows-append mitigation P3b.0 established, for the same
             tab-order reason: a header button sits BEFORE the rows it appends to. */
          actions.focusLastIn('shifts', '[aria-label$=" start"]');
        }}
      >
        {RTC_COPY.addShift}
      </Button>
    ),
  },
  {
    id: 'window',
    step: 'what',
    title: 'When it runs',
    tooltip: RTC_COPY.rolling,
    validate: ({ draft }) => ({
      blockers: draft.startDate === '' ? ['Set the date it starts.'] : [],
      warnings: [],
    }),
    render: (state) => (
      <RunsForSection
        draft={state.draft}
        readOnly={state.readOnly}
        endsKind={state.endsKind}
        horizonDays={state.horizonDays}
        fillOpenLeadHours={state.fillOpenLeadHours}
      />
    ),
  },
  {
    id: 'force',
    step: 'force',
    title: 'Force required',
    tooltip: RTC_COPY.oneRequirement,
    /**
     * ⚠️ [P3b.3] "STILL TO FIND" IS A WARNING. AN UNADDRESSED COLUMN IS THE BLOCKER.
     *
     * A column asking for 10 with 6 found is a real order that has to go out — on day one
     * the establishment is not mapped and a division cannot always promise the number. What
     * genuinely leaves a deployment undefined is a column with NO division against it: a
     * request addressed to nobody, which P4 has nothing to route.
     */
    validate: ({ grid }) => {
      const asked = grid.totals.some((total) => total > 0);
      const remaining = remainingByColumn(grid);
      const unaddressed = grid.totals.some(
        (total, lineIndex) => total > 0 && !grid.cells.some((row) => (row[lineIndex] ?? 0) > 0),
      );
      const balanced = asked && remaining.every((value) => value === 0);
      return {
        blockers: [
          ...(asked ? [] : ['Ask for at least one person.']),
          ...(unaddressed ? ['Say which division provides each column.'] : []),
        ],
        warnings: [
          ...(asked && remaining.some((value) => value > 0) ? ['Some columns are still short.'] : []),
          ...(asked && remaining.some((value) => value < 0)
            ? ['Some columns have more allocated than asked for.']
            : []),
        ],
        detail: balanced ? 'Balanced' : 'Still to source',
      };
    },
    render: (state, actions) => (
      <div className="flex flex-col gap-3">
        <p className="text-xs text-muted-foreground">{RTC_COPY.oneRequirement}</p>
        <p className="text-xs text-muted-foreground">{RTC_COPY.divisionsDerived}</p>
        {/* ⚠️ REGISTERED, THOUGH NOTHING RENDERS IT — `Form.useWatch` sees registered fields only. */}
        <Form.Item name="requirementLines" hidden>
          <input type="hidden" />
        </Form.Item>
        <ForceGrid
          grid={state.grid}
          orgUnits={state.orgUnits}
          readOnly={state.readOnly}
          onChange={(next) => {
            actions.setRequirementLines(fromGrid(next, state.draft.requirementLines));
          }}
        />
        {/* ⚠️ The answer to "where is supervision?", where the question is asked. */}
        <NoCommandNote />
      </div>
    ),
  },
  {
    /* ⚠️ THE PA INTAKE'S OWN COMPONENT, RENDERED UNCHANGED. */
    id: 'extras',
    step: 'orders',
    title: 'Instructions and Supporting Arrangements',
    navTitle: 'Instructions',
    validate: ({ draft }) => ({
      blockers: [],
      warnings: draft.instructionCount > 0 ? [] : ['No instructions have been added.'],
      detail: 'Optional',
    }),
    render: (state) => <ExtrasSection readOnly={state.readOnly} />,
  },
];
